import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
  Button,
  Stack,
  Text,
  Badge,
  SimpleGrid,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Card, CardBody, CardHeader } from '@chakra-ui/react';
import CreateVehicleModal from './CreateVehicleModal';
import { postData } from '../../utils/httpClient';

const VehicleListModal = ({
  isOpen,
  closeVehicleListModal,
  vehiclesList,
  matchedCustomers = [],
  handleSelectVehicle,
  onVehicleCreated,
}) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    vehicle: null,
    isNew: false,
    customer: null,
  });
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const badgeColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const toast = useToast();

  const openNewVehicleModal = (customer = null) => {
    setModalState({
      isOpen: true,
      vehicle: { id: null, name: '' },
      isNew: true,
      customer,
    });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false, vehicle: null, isNew: false, customer: null });
  };

  const handleCreateVehicle = async (newVehicle) => {
    try {
      const response = await postData('/vehicles', newVehicle);
      
      // Si se proporcionó una función callback, llamarla con el nuevo vehículo
      if (onVehicleCreated && response?.payload?.vehicle) {
        onVehicleCreated(response.payload.vehicle);
      }
      
      closeEditModal();
      closeVehicleListModal(); // También cerrar el modal de selección
      
      toast({
        title: 'Vehículo creado',
        description: 'El vehículo se ha creado correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al crear vehículo',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeVehicleListModal}
      size="4xl"
      isCentered
     closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent
        bg={cardBg}
        borderRadius="xl"
        boxShadow="2xl"
        maxH="90vh"
        overflow="hidden"
      >
        <ModalHeader fontSize="2xl" fontWeight="bold" textAlign="center">
          Seleccionar Vehículo
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody overflowY="auto" px={6}>
          {vehiclesList && vehiclesList.length > 0 ? (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
              {[...vehiclesList]
                .sort((a, b) => a.plate.localeCompare(b.plate))
                .map((vehicle) => (
                  <Card
                    key={vehicle.id}
                    bg={cardBg}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                    boxShadow="sm"
                    transition="all 0.2s"
                    _hover={{
                      bg: hoverBg,
                      transform: 'translateY(-2px)',
                      boxShadow: 'md',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleSelectVehicle(vehicle)}
                  >
                    <CardHeader px={3} pt={3} pb={1}>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="md" fontWeight="bold">
                          {vehicle.plate || 'Sin matrícula'}
                        </Text>
                        <Badge colorScheme="gray" fontSize="xs">
                          {vehicle.year || 'N/A'}
                        </Badge>
                      </Flex>
                    </CardHeader>
                    <CardBody px={3} pt={1} pb={3}>
                      <Stack spacing={0.5}>
                        <Text fontSize="xs" noOfLines={1}>
                          <strong>Modelo:</strong> {vehicle.model?.description || 'N/A'}
                        </Text>
                        <Text
                          fontSize="xs"
                          noOfLines={1}
                          title={vehicle.chassisNumber || ''}
                        >
                          <strong>Chasis:</strong> {vehicle.chassisNumber || 'N/A'}
                        </Text>
                        <Text fontSize="xs" color={badgeColor} noOfLines={1}>
                          <strong>Cliente:</strong>{' '}
                          {vehicle.currentCustomer?.name ||
                            vehicle.customer?.name ||
                            'N/A'}
                        </Text>
                      </Stack>
                    </CardBody>
                  </Card>
                ))}
            </SimpleGrid>
          ) : matchedCustomers.length > 0 ? (
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                El cliente existe, pero no tiene vehículos cargados. Creá el vehículo para poder agendar.
              </Text>
              {matchedCustomers.map((customer) => (
                <Card
                  key={customer.id}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                  boxShadow="sm"
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() => openNewVehicleModal(customer)}
                >
                  <CardBody px={4} py={3}>
                    <Text fontWeight="bold">{customer.name}</Text>
                    <Text fontSize="sm" color={badgeColor}>
                      Documento: {customer.document || 'Sin documento'}
                    </Text>
                  </CardBody>
                </Card>
              ))}
            </Stack>
          ) : (
            <Text textAlign="center" color="gray.500">
              No se encontraron vehículos.
            </Text>
          )}
        </ModalBody>

        <ModalFooter>
          <Flex justify="space-between" align="center" width="100%">
            <Button
              colorScheme="gray"
              onClick={() => openNewVehicleModal(matchedCustomers[0] || null)}
            >
              Nuevo Vehículo
            </Button>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={closeVehicleListModal}
            >
              Cerrar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      
      <CreateVehicleModal
        isOpen={modalState.isOpen}
        onClose={closeEditModal}
        onCreate={handleCreateVehicle}
        initialCustomer={modalState.customer}
      />
    </Modal>
  );
};

export default VehicleListModal;
